import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import Button, { BUTTON_VARIANTS } from "../../components/shared/Button/Button";
import { MyInput } from "pages/StepPages/StepTwo/MyInput/MyInput";
import { ClearError, clearVerifyEmail } from "toolkit/profile/profile.slice";
import { changePassword, login, verifyEmail } from "toolkit/profile/profile.thunks";
import { ReactComponent as CheckIcon } from "images/check.svg";
import cn from "classnames";
import "./LoginPage.styles.sass";

export const LoginPage: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [form, setForm] = useState<{ email: string; password: string; confirmPassword: string }>({
		email: "",
		password: "",
		confirmPassword: "",
	});

	const { authed, isVerifyEmail, error } = useAppSelector((state) => state.profile);
	const [finish, setFinish] = useState<boolean>(false);
	const [memorization, setMemorization] = useState<boolean>(false);
	const [submitError, setSubmitError] = useState<boolean>(false);

	const customDispatch = (user: { email: string; password: string; memorization: boolean }) => {
		dispatch(login(user));
	};

	const validatePassword = useMemo((): boolean => {
		const regExp = "^(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$";
		if (!form.password.match(regExp)) return false;
		return true;
	}, [form]);

	const validateEmail = useMemo((): boolean => {
		const regExp = "^[_A-Za-z0-9-+]+(.[_A-Za-z0-9-]+)*@[A-Za-z0-9-]+(.[A-Za-z0-9]+)*(.[A-Za-z]{2,})$";
		if (!form.email.match(regExp) || !form.email.includes(".") || form.email.includes(" ")) return false;
		return true;
	}, [form]);

	const loginHandler = useCallback(
		(e: React.FormEvent<HTMLFormElement>): void => {
			e.preventDefault();
			if (isVerifyEmail === null) {
				if (validateEmail) {
					dispatch(verifyEmail(form.email));
					setSubmitError(false);
				} else {
					setSubmitError(true);
				}
			} else if (isVerifyEmail) {
				if (validatePassword) {
					customDispatch({ ...form, memorization });
					setSubmitError(false);
				} else {
					setSubmitError(true);
				}
			} else {
				if (form.password === form.confirmPassword) {
					setSubmitError(false);
					if (validatePassword) {
						onFinish();
						dispatch(changePassword({ email: form.email, newPassword: form.password }));
						setSubmitError(false);
					} else {
						setSubmitError(true);
					}
				} else {
					if (validatePassword) {
						setSubmitError(false);
					} else {
						setSubmitError(true);
					}
				}
			}
			//eslint-disable-next-line
		},[isVerifyEmail, validateEmail, validatePassword, form],
	);

	useEffect(() => {
		if (authed) {
			navigate("/organizations");
		}
		dispatch(ClearError());
		//eslint-disable-next-line
	}, [authed]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>, name: keyof typeof form) => {
		const { value } = e.target;

		if (submitError) {
			setSubmitError(false);
		}

		setForm((prevState) => ({ ...prevState, [name]: value }));

		if (error.status) dispatch(ClearError());
	};

	const onFinish = () => {
		setFinish(true);
		dispatch(clearVerifyEmail());
	};

	const startWorkin = () => {
		setFinish(false);
		setForm({ email: "", password: "", confirmPassword: "" });
		customDispatch({ email: form.email, password: form.password, memorization });
	};

	const memorizationHandler = () => {
		setMemorization(!memorization);
	};

	return (
		<div className="authentication login">
			{!finish ? (
				<>
					<h3 className="authentication-title">{isVerifyEmail === false ? "Ваш пароль сброшен" : "Вход"}</h3>
					<form onSubmit={loginHandler} className="authentication-form">
						{isVerifyEmail === null ? (
							<MyInput
								name="Почта"
								placeholder="address@domain.ru"
								value={form.email}
								set={(e) => handleChange(e, "email")}
								required
								condition={(submitError && !validateEmail) || error?.status === 401}
								warning={{
									condition: submitError && !validateEmail,
									text: "Некорректный email",
								}}
							/>
						) : isVerifyEmail ? (
							<>
								<MyInput
									name="Пароль"
									placeholder="**********"
									value={form.password}
									set={(e) => handleChange(e, "password")}
									type="password"
									required
									condition={(submitError && !validatePassword) || error?.status === 401}
									warning={{
										condition: (submitError && !validatePassword) || error?.status === 401,
										text: error?.message || "Условия не соблюдены",
									}}
								/>
								<div className={cn("passInfo", { passError: submitError })}>
									Пароль должен состоять минимум из 8 символов, должна быть минимум одна заглавная буква и не меньше
									одной цифры
								</div>
							</>
						) : (
							<>
								<div className="authentication-txt">Введите новый пароль</div>
								<MyInput
									name="Пароль"
									placeholder="**********"
									value={form.password}
									set={(e) => handleChange(e, "password")}
									type="password"
									required
									condition={(submitError && !validatePassword) || error?.status === 401}
									warning={{
										condition: (submitError && !validatePassword) || error?.status === 401,
										text: error?.message || "Условия не соблюдены",
									}}
								/>
								<div className={cn("passInfo", { passError: submitError })}>
									Пароль должен состоять минимум из 8 символов, должна быть минимум одна заглавная буква и не меньше
									одной цифры
								</div>
								<MyInput
									className="inp"
									name="Подтверждение пароля"
									placeholder="**********"
									value={form.confirmPassword}
									set={(e) => handleChange(e, "confirmPassword")}
									type="password"
									required
									condition={form.password !== form.confirmPassword}
								/>
							</>
						)}
						<div className="authentication-form-toggler-wrp">
							<div className="authentication-form-toggler-memorization">
								<label htmlFor="memorization">
									<input checked={memorization} onChange={memorizationHandler} type="checkbox" id="memorization" />
									Запомнить меня
								</label>
							</div>
							<Button to="/registration" variant={BUTTON_VARIANTS.TEXT}>
								У меня нет аккаунта
							</Button>
						</div>
						<Button type="submit" full>
							{isVerifyEmail === null ? "Далее" : isVerifyEmail ? "Войти" : "Готово"}
						</Button>
					</form>
				</>
			) : (
				<>
					<CheckIcon className="checkIcon" />
					<h3 className="authentication-title">Вы сменили пароль</h3>
					<Button type="submit" full onClick={startWorkin}>
						Начать работу
					</Button>
				</>
			)}
		</div>
	);
};
